import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { useProjectStore } from './projectStore';
import {
  fetchCollectionsForUser,
  importLegacyLocalStorage,
  loadCollectionAsProject,
  createCollectionRecord,
  insertDesignRecord,
  linkOrphanedDataToCollection,
  claimUnscopedStockToCollection,
  getLastCollectionId,
  setLastCollectionId,
  clearLastCollectionId,
} from '../lib/collectionPersistence';
import { migrateLocalFloorplans } from '../lib/floorplanPersistence';
import { registerCollectionRef } from './collectionRef';
import type { Project } from '../types';
import {
  readSavedAccountUiPrefs,
  resolveAccountUiPrefs,
  settingsForNewCollection,
} from '../utils/accountUiPrefs';
import { startCollectionRealtime, stopCollectionRealtime } from '../lib/realtime/collectionSync';

interface CollectionState {
  currentCollectionId: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  initialize: (userId: string) => Promise<void>;
  reset: () => void;
  switchCollection: (collectionId: string) => Promise<void>;
  createBlankCollection: (
    name: string,
    customerName: string,
    address: string
  ) => Promise<string | null>;
}

const hydrateProjectStore = (project: Project) => {
  useProjectStore.getState().hydrateProject(project);
  setLastCollectionId(project.id);
};

/** Fetch + migrate floorplans for a collection in the background (non-blocking). */
const loadFloorplansForCollection = (collectionId: string) => {
  migrateLocalFloorplans(collectionId)
    .then(() => useProjectStore.getState().fetchAndCacheFloorplans(collectionId))
    .catch((err) => console.warn('[loadFloorplansForCollection]', err));
};

export const useCollectionStore = create<CollectionState>((set, get) => ({
  currentCollectionId: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  initialize: async (userId: string) => {
    if (get().isInitialized && get().currentCollectionId) return;

    set({ isLoading: true, error: null });

    try {
      let collections = await fetchCollectionsForUser(userId);

      if (collections.length === 0) {
        const importedId = await importLegacyLocalStorage(userId);
        if (importedId) {
          collections = await fetchCollectionsForUser(userId);
        }
      }

      await useProjectStore.getState().refreshAvailableProjectsFromCloud(userId);

      if (collections.length === 0) {
        set({ isLoading: false, isInitialized: true, currentCollectionId: null });
        return;
      }

      const lastId = getLastCollectionId();
      const target =
        collections.find((c) => c.id === lastId) ??
        collections[0];

      await linkOrphanedDataToCollection(target.id);

      const project = await loadCollectionAsProject(target.id);
      if (project) {
        hydrateProjectStore(project);
        loadFloorplansForCollection(target.id);
        set({
          currentCollectionId: target.id,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
        const uid = useAuthStore.getState().user?.id;
        if (uid) startCollectionRealtime(target.id, uid);
      } else {
        set({ isLoading: false, isInitialized: true, error: null });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load collections';
      console.error('Collection initialize failed:', err);
      set({ isLoading: false, isInitialized: true, error: message });
    }
  },

  reset: () => {
    stopCollectionRealtime();
    clearLastCollectionId();
    useProjectStore.getState().stopAutosaveTimer();
    useProjectStore.setState({
      currentProject: null,
      availableProjects: [],
      openDesigns: [],
      activeDesignId: null,
      currentDesignId: null,
      cachedFloorplans: [],
    });
    set({
      currentCollectionId: null,
      isLoading: false,
      isInitialized: false,
      error: null,
    });
  },

  switchCollection: async (collectionId: string) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return;

    set({ isLoading: true, error: null });
    try {
      const project = await loadCollectionAsProject(collectionId);
      if (!project) throw new Error('Collection not found');

      await linkOrphanedDataToCollection(collectionId);

      hydrateProjectStore(project);
      loadFloorplansForCollection(collectionId);
      set({ currentCollectionId: collectionId, isLoading: false, error: null });
      startCollectionRealtime(collectionId, userId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to switch collection';
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  createBlankCollection: async (name, customerName, address) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return null;

    const customer = { name: customerName, address };
    const designerPrefs = readSavedAccountUiPrefs(userId) ?? resolveAccountUiPrefs(userId);
    const collection = await createCollectionRecord(
      userId,
      name,
      customer,
      settingsForNewCollection(designerPrefs)
    );
    const now = new Date();
    const defaultDesign = {
      id: collection.id,
      name,
      createdAt: now,
      updatedAt: now,
      racks: [],
      equipment: [],
      cables: [],
    };

    await insertDesignRecord(collection.id, defaultDesign, 0);

    const project = await loadCollectionAsProject(collection.id);
    if (project) {
      hydrateProjectStore(project);
      loadFloorplansForCollection(collection.id);
      await useProjectStore.getState().refreshAvailableProjectsFromCloud(userId);
      set({ currentCollectionId: collection.id, isInitialized: true });
      startCollectionRealtime(collection.id, userId);
      return collection.id;
    }

    return null;
  },
}));

export function getActiveCollectionId(): string | null {
  return (
    useCollectionStore.getState().currentCollectionId ??
    useProjectStore.getState().currentProject?.id ??
    null
  );
}

// Register the collection store into the shared bridge so projectStore can
// call switchCollection / clear state without a direct circular import.
registerCollectionRef({
  switchCollection: (id) => useCollectionStore.getState().switchCollection(id),
  getCollectionId:  ()  => useCollectionStore.getState().currentCollectionId,
  setPartialState:  (p) => useCollectionStore.setState(p),
});
